import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import CustomModal from "@components/CustomModal";

import api from "@helpers/api";

import "./styles.scss";

import { dispatchEvent } from "@hooks/useEvent";

import {
    useUploadCommunityAsset,
    AssetCategory,
    isValidHttpUrl,
} from "@features/uploadAsset";
import { readVideoMetadata } from "@features/videoUpload";

import { Text, Button } from "@fragments";
import { DownloadRounded, TuneRounded } from "@mui/icons-material";

import videoHelpers from "@helpers/video";
import EVENTS from "./events";

import Video from "./sections/Video";
import Title from "./sections/Title";
import Description from "./sections/Description";
import LessonOwner from "./sections/LessonOwner";
import SelectCategories from "./sections/SelectCategories";
import Attachments from "./sections/Attachments";
import Availability from "./sections/Availability";
import WaitDaysAfterPayment from "./sections/WaitDaysAfterPayment";
import Launch from "./sections/Launch";

async function dataUrlToFile(dataUrl, filename) {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || "image/jpeg" });
}

// The editor lays a video out in one of two shapes: 16:9 or 9:16.
function snapAspectRatio(ratio) {
    return Math.abs(ratio - 0.56) < Math.abs(ratio - 1.78) ? 0.56 : 1.78;
}

function focusFieldTarget(ref) {
    if (!ref?.current) return;
    requestAnimationFrame(() => {
        const el = ref.current;
        el.scrollIntoView?.({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") {
            try {
                el.focus({ preventScroll: true });
            } catch {
                el.focus();
            }
        }
    });
}

export default function ManageContentModal({ modalRef }) {
    const { t } = useTranslation("contentModals");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadBusyCount, setUploadBusyCount] = useState(0);

    const REF_config = useRef({});
    const REF_callback = useRef();

    const refFocusTitle = useRef(null);
    const refFocusDescription = useRef(null);
    const refFocusAvailability = useRef(null);

    // Video and banner uploads both count: Save stays disabled until every
    // file has landed, so the content never points at a missing file.
    const handleUploadBusyChange = (uploading) => {
        setUploadBusyCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    };
    const uploading = uploadBusyCount > 0;

    // Closing unmounts the body, which cancels the uploads still running.
    function handleClose() {
        if (!uploading) {
            setOpen(false);
            return;
        }

        window.openConfirm({
            title: t("manageContent.leaveUpload.title"),
            subtitle: t("manageContent.leaveUpload.subtitle"),
            buttons: [
                {
                    text: t("manageContent.leaveUpload.keepUploading"),
                    variant: "contained",
                    color: "primary",
                },
                {
                    text: t("manageContent.leaveUpload.close"),
                    onClick: () => setOpen(false),
                    variant: "text",
                    color: "error",
                },
            ],
        });
    }

    modalRef.current = {
        open(preFill, callback = () => { }) {
            const bannerRaw = preFill.banner;
            const banner =
                bannerRaw != null && String(bannerRaw).trim()
                    ? String(bannerRaw).trim()
                    : null;

            REF_config.current = {
                ...preFill,
                availability: preFill.availability || {},
                video: {
                    aspect_ratio: preFill.video?.aspect_ratio || 16 / 9,
                    src: preFill.video?.src || null,
                    duration: preFill.video?.duration || 0,
                },
                banner,
            };
            delete REF_config.current.banner_base64;
            REF_callback.current = callback;
            setOpen(true);
        },
    };

    // The video is optional: a content can be saved before it is recorded.
    function validateContentForm() {
        const { name, description, availability } = REF_config.current;
        const trimmedName = name?.trim?.() ?? "";

        if (!trimmedName || trimmedName.length < 3 || trimmedName.length > 100) {
            window.setSnackbar(
                t("manageContent.validation.name")
            );
            focusFieldTarget(refFocusTitle);
            return false;
        }

        if (description != null && description.length > 1000) {
            window.setSnackbar(
                t("manageContent.validation.description")
            );
            focusFieldTarget(refFocusDescription);
            return false;
        }

        const plans = availability?.plans;
        if (Array.isArray(plans) && plans.length === 0) {
            window.setSnackbar(
                t("manageContent.validation.plans")
            );
            focusFieldTarget(refFocusAvailability);
            return false;
        }

        return true;
    }

    async function handleSubmit() {
        if (uploading) return;

        try {
            if (!validateContentForm()) {
                return;
            }

            const payload = getPayload();

            setLoading(true);

            const contentId = REF_config.current.id;
            const { data } = await (contentId ? api.patch(`content/${contentId}`, payload) : api.post("content", payload));

            if (data.id) {
                await REF_callback.current(data);
                setOpen(false);
            }
        } catch (e) {
            console.log(e);
            window.setSnackbar(t("manageContent.feedback.saveError"));
        }

        setLoading(false);

        function getPayload() {
            const {
                id,
                name,
                description,
                availability,
                section,
                playlist,
                video,
                attachments,
                creator,
                categories,
                banner,
                timestamps
            } = REF_config.current;

            const videoSrc = typeof video === 'string' ? video : video?.src;

            // Section/playlist ids are hashed slugs (`sc-…`, `pl-…`); the API
            // resolves them itself, so they must be sent as-is (parseInt → NaN → null).
            const data = {
                section,
                name: name?.trim(),
                description: description?.trim(),
                creator: creator?.id,
                // No video: core reads `src: ""` as "clear it" and `null` as
                // "keep the stored one"; ratio and duration only travel with a video.
                video: videoSrc
                    ? {
                        src: videoSrc,
                        aspect_ratio: video?.aspect_ratio,
                        duration: video?.duration
                    }
                    : { src: "" },
                availability: {
                    wait_days_after_purchase: availability.wait_days_after_purchase,
                    plans: availability.plans ? availability.plans[0] === 'all' ? null : availability.plans : null,
                    launch_at: availability?.launch_at || timestamps?.launch
                },
                attachments: attachments || [],
                categories: categories || [],
            };

            if (banner && isValidHttpUrl(banner)) {
                data.banner = banner;
            }

            if (!!playlist) {
                data.playlist = playlist;
            }

            if (!!id) {
                data.id = id;
            }

            return data;
        }
    }

    async function handleDelete() {
        try {
            const { data } = await api.delete(`content/${REF_config.current.id}`);
            window.setSnackbar(t("manageContent.feedback.deleted"));
            REF_callback.current();
            setOpen(false);
        } catch (e) {
            console.log(e);
        }
    }

    async function handleDownload() {
        try {
            const source = REF_config.current.video.src;

            if (!source) {
                window.setSnackbar(t('manageContent.feedback.noVideo'));
                return;
            }

            // Check if the source has .m3u8 extension
            if (!source.endsWith('.m3u8')) {
                // If it's not .m3u8, download as is
                downloadFile(source);
                return;
            }

            // Replace .m3u8 with .mp4 or .mov to get the original video
            const baseUrl = source.replace('.m3u8', '');
            const mp4Url = `${baseUrl}.mp4`;
            const movUrl = `${baseUrl}.mov`;

            // Try .mp4 first
            const mp4Exists = await checkFileExists(mp4Url);
            if (mp4Exists) {
                await downloadFile(mp4Url);
                return;
            }

            // Try .mov if .mp4 doesn't exist
            const movExists = await checkFileExists(movUrl);

            if (movExists) {
                await downloadFile(movUrl);
                return;
            }

            // If neither exists, show error
            window.setSnackbar(t('manageContent.feedback.originalNotFound'));
        } catch (e) {
            console.log(e);
            window.setSnackbar(t('manageContent.feedback.downloadError'));
        }
    }

    async function checkFileExists(url) {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
        } catch (e) {
            return false;
        }
    }

    async function downloadFile(url) {
        try {
            window.setSnackbar(t('manageContent.feedback.downloadStarting'));

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to download file');
            }

            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = url.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);

            window.setSnackbar(t('manageContent.feedback.downloading'));
        } catch (e) {
            console.error('Download error:', e);
            window.setSnackbar(t('manageContent.feedback.downloadError'));
        }
    }

    return (
        <>
            <CustomModal
                maxWidth={670}
                bottomButtonSumbitText={
                    uploading
                        ? t("manageContent.submitWaitingUpload")
                        : t("manageContent.submitButton")
                }
                bottomButtonCancelText={t("common:actions.cancel")}
                loading={loading}
                disabled={uploading}
                title={t("manageContent.modalTitle")}
                deletionTitle={t("manageContent.deleteConfirmTitle")}
                handleDelete={handleDelete}
                handleSubmit={handleSubmit}
                topActions={
                    REF_config.current.id && REF_config.current?.video?.src && (
                        <Button
                            onClick={handleDownload}
                            color="overlay"
                            startIcon={<DownloadRounded fontSize="small" />}
                            className="mr-2"
                        >
                            {t("manageContent.downloadButton")}
                        </Button>
                    )}
                open={open}
                handleClose={handleClose}
                bodyComponent={
                    open ? (
                        <Body
                            configRef={REF_config}
                            onUploadBusyChange={handleUploadBusyChange}
                            refFocusTitle={refFocusTitle}
                            refFocusDescription={refFocusDescription}
                            refFocusAvailability={refFocusAvailability}
                        />
                    ) : null
                }
            />
        </>
    );
}



function Body({
    configRef,
    onUploadBusyChange,
    refFocusTitle,
    refFocusDescription,
    refFocusAvailability,
}) {
    const { t } = useTranslation("contentModals");
    const [, setTick] = useState(0);
    const bump = () => setTick((n) => n + 1);

    const communityId = configRef.current.community
        ? parseInt(configRef.current.community, 10)
        : null;

    const { uploadFile } = useUploadCommunityAsset({
        communityId,
        onUploadingChange: onUploadBusyChange,
    });

    function handleBannerUrlChange(url) {
        configRef.current.banner = url;
        configRef.current.banner_base64 = undefined;
        bump();
    }

    // Called only once the upload's PUT succeeded (or with null on remove), so
    // the saved `src`, ratio and duration always describe a file in the bucket.
    function handleVideoChange(url, metadata) {
        if (!url) {
            configRef.current.video = { ...configRef.current.video, src: null };
            return;
        }

        const video = { ...configRef.current.video, src: url };
        if (metadata) {
            video.duration = Math.round(metadata.duration);
            video.aspect_ratio = snapAspectRatio(metadata.aspectRatio);
            dispatchEvent(EVENTS.ASPECT_RATIO_CHANGE, video.aspect_ratio);
        }
        configRef.current.video = video;
    }

    // A file was picked: lay the box out in its shape while it uploads, and
    // make a banner from its first second when the content has none.
    async function handleVideoSelected(localUrl) {
        readVideoMetadata(localUrl).then((metadata) => {
            if (metadata) {
                dispatchEvent(EVENTS.ASPECT_RATIO_CHANGE, snapAspectRatio(metadata.aspectRatio));
            }
        });

        if (configRef.current.banner || !communityId) return;

        let thumb = null;
        try {
            thumb = await videoHelpers.generateThumbFromLocalVideo(localUrl);
        } catch (e) {
            console.error(e);
        }
        if (!thumb) return;

        try {
            const file = await dataUrlToFile(thumb, "thumb.jpg");
            const url = await uploadFile(file, AssetCategory.CONTENT_BANNER);
            // The creator may have picked a banner meanwhile; theirs wins.
            if (configRef.current.banner) return;
            configRef.current.banner = url;
            configRef.current.banner_base64 = undefined;
            bump();
        } catch (e) {
            console.error(e);
            window.setSnackbar?.(
                t("manageContent.feedback.bannerUploadError")
            );
        }
    }

    const handlerController = {
        handleChangeName(value) {
            configRef.current.name = value;
        },
        handleChangeDescription(value) {
            configRef.current.description = value;
        },
        handleChangeOwner(value) {
            configRef.current.creator = { id: value };
        },

        handleChangeCategories(value) {
            configRef.current.categories = value;
        },

        handleChangeAttachments(value) {
            configRef.current.attachments = value;
        },
        handleChangeAvailability(value) {
            configRef.current.availability.plans = value;
            dispatchEvent(EVENTS.AVAILABILITY_CHANGE, value);
        },
        handleChangeWaitDays(value) {
            configRef.current.availability.wait_days_after_purchase = value;
        },
        handleChangeLaunch(value) {
            configRef.current.timestamps.launch = value;
        }
    };

    return (
        <div className="lesson-config-box mt-1 px-4 py-1">
            <Video
                communityId={communityId}
                bannerUrl={configRef.current.banner}
                onBannerChange={handleBannerUrlChange}
                onBannerUploadingChange={onUploadBusyChange}
                defaultAspectRatio={configRef.current.video?.aspect_ratio}
                defaultVideoUrl={configRef.current.video?.src}
                onVideoChange={handleVideoChange}
                onVideoSelected={handleVideoSelected}
                onVideoUploadingChange={onUploadBusyChange}
                contentId={configRef.current.id}
                getContentName={() => configRef.current.name}
            />

            <Text.h3
                value={t("manageContent.generalSettings")}
                left={<TuneRounded />}
                className="mb-2 mt-3"
            />
            <Title
                ref={refFocusTitle}
                value={configRef.current.name}
                onInput={handlerController.handleChangeName}
            />
            <Description
                ref={refFocusDescription}
                value={configRef.current.description}
                onInput={handlerController.handleChangeDescription}
            />

            <LessonOwner
                key={`${configRef.current.community}-${configRef.current.creator?.id}`}
                value={configRef.current.creator?.id}
                onChange={handlerController.handleChangeOwner}
                community={configRef.current.community}
            />

            <SelectCategories
                value={configRef.current.categories}
                onChange={handlerController.handleChangeCategories}
            />

            <Attachments
                value={configRef.current.attachments}
                onChange={handlerController.handleChangeAttachments}
            />

            <div ref={refFocusAvailability} tabIndex={-1}>
                <Availability
                    value={configRef.current.availability}
                    onChange={handlerController.handleChangeAvailability}
                    community={configRef.current.community}
                />
            </div>

            <WaitDaysAfterPayment
                value={configRef.current.availability?.wait_days_after_purchase}
                onChange={handlerController.handleChangeWaitDays}
                initialHasPlans={configRef.current.availability?.plans?.length > 0}
            />

            <Launch
                value={configRef.current.timestamps?.launch}
                onChange={handlerController.handleChangeLaunch}
            />
        </div>
    );
}
